/**
 * Calendar-date helpers for the day-schedule surfaces.
 *
 * ``Date.prototype.toISOString()`` reports the date in UTC, so comparing
 * "is this the same day / a later day" with ``toISOString().slice(0, 10)``
 * is wrong for any local evening: in UTC+2 (Europe/Stockholm, summer) a
 * midnight-normalised selection of TOMORROW lands at 22:00Z of the
 * PREVIOUS UTC day, so from 22:00 local onward "tomorrow" compared as the
 * SAME day as today, its slots got graded against the current wall clock
 * and tomorrow's late evening was greyed out as past — nothing bookable.
 * The mirror case: just after local midnight the UTC date is still
 * yesterday, so today's already-past slots stopped greying at all.
 *
 * These helpers compare the LOCAL calendar date instead. Our surfaces are
 * single-tenant: the browser runs in the site timezone and every other
 * schedule helper (``slotToIso``, ``minutesFromDayStart``, the row grid's
 * ``slotIndex``) already indexes the slots by the same local reads, so the
 * local calendar IS the site-timezone calendar the day is drawn in.
 *
 * Kept dependency-free (``date-fns`` stays out of this module on purpose —
 * see the header comment in ``RoomDaySchedule.tsx``).
 */

/** Local calendar date as `YYYY-MM-DD` (NOT the UTC date). */
export function localDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Index of the first slot that isn't in the past on ``selectionDate`` as
 *  seen at the wall-clock reading ``nowMs``. The reading is passed in
 *  (rather than read here) so callers and tests pin the exact moment — the
 *  same pure-clock convention as ``time.ts``. A later local day is never
 *  past (0), an earlier one is always past (``totalSlots``); on the selected
 *  day itself we FLOOR (not ceil) so the in-progress quarter stays bookable
 *  — see ``RoomDaySchedule``'s ``clampStartToNow``. */
export function pastSlotThreshold(
  selectionDate: Date,
  nowMs: number = Date.now(),
  dayStartHour = 0,
  slotMinutes = 15,
  totalSlots = ((24 - dayStartHour) * 60) / slotMinutes,
): number {
  const now = new Date(nowMs);
  const todayKey = localDateKey(now);
  const dateKey = localDateKey(selectionDate);
  if (dateKey > todayKey) return 0;
  if (dateKey < todayKey) return totalSlots;
  const minutesSinceDayStart = (now.getHours() - dayStartHour) * 60 + now.getMinutes();
  return Math.max(0, Math.floor(minutesSinceDayStart / slotMinutes));
}
